export default function HeicveditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - HEIC Viewer &amp; Converter
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for HEIC Viewer &amp;
            Converter.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What is a HEIC file and why won&apos;t it open?
              </h3>
              <p className="mt-1">
                HEIC (and HEIF) is the high-efficiency photo format that newer
                phones use. Many Android galleries, chat apps, and computers
                can&apos;t open it directly, so a photo you received may show up
                as a file you can&apos;t preview. This app opens those photos
                and lets you convert them to a format everything understands.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I view a HEIC photo?
              </h3>
              <p className="mt-1">
                Tap <strong>Open / View HEIC</strong> on the home screen and
                pick a .heic or .heif file. The photo opens on screen, and you
                can pinch to zoom. You can also open a HEIC file from another
                app with <strong>Open with</strong> and choose HEIC Viewer
                &amp; Converter.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I convert HEIC to JPG, PNG, or PDF?
              </h3>
              <p className="mt-1">
                Tap <strong>Convert HEIC</strong>, pick one or more photos,
                choose your output format (JPG, PNG, or PDF), set the JPG
                quality if you like, and tap Convert. The conversion runs on
                your device. When it finishes you can share the results or save
                them with the system save dialog.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Can I convert many photos at once?
              </h3>
              <p className="mt-1">
                Yes. On the Convert screen, select multiple .heic or .heif files
                and they will all be converted in one batch, with progress shown
                as it goes. Any file that can&apos;t be read is reported
                separately so the rest still finish.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does everything work offline?
              </h3>
              <p className="mt-1">
                Yes. Viewing and converting both run entirely on your device.
                Network access is used only for showing ads and for the rotating
                cross-promotion banner. Your photos never leave your phone.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Which photos are supported?
              </h3>
              <p className="mt-1">
                Single-image .heic and .heif photos are supported, including
                those from recent phones. Some files store more than one image
                or use uncommon encodings; if a particular photo won&apos;t open,
                email it to us and we will take a look.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all ads
                with a one-time Pro upgrade from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing or a photo won&apos;t open. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app, and make sure you have the
                latest version from Google Play. If one specific photo causes
                the problem, email us with the file attached so we can reproduce
                it.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Support</h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Important Links</h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/heicvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/heicvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/heicvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/heicvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">App Information</h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> HEIC Viewer &amp; Converter
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.heicvedit
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
